import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { HttpService, HttpRequestModel } from 'src/app/service/httpservice/http.service';
import { ToolSearchParams } from 'src/app/shared/models/toolSearchParams';
import { Observable, BehaviorSubject } from 'rxjs';
import { buildQueryString } from 'src/app/utils/functions';

interface SelectedDataModel {
  id: number;
  jobNumber: string;
  name: string;
  potentialJobDetail?: Object;
  selected?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ToolsService {
  HttpRequestModel: HttpRequestModel = new HttpRequestModel();

  private totalCountSource = new BehaviorSubject(0);
  currentTotalCount = this.totalCountSource.asObservable();

  private totalCountLabelSource = new BehaviorSubject('');
  currentTotalCountLabel = this.totalCountLabelSource.asObservable();

  private countShouldBeVisibleSource = new BehaviorSubject(true);
  countShouldBeVisible = this.countShouldBeVisibleSource.asObservable();

  private selectedItemsSource = new BehaviorSubject<Array<SelectedDataModel>>([]);
  selectedItems = this.selectedItemsSource.asObservable();

  constructor(private httpService: HttpService) { }

  private defaultParams: ToolSearchParams = {
    page: 1,
    limit: 20,
    search: '',
    fields: '',
    order: 'toolId,desc',
    cache: 'skip',
    embed: 'media',
  };

  defaultShopParams: ToolSearchParams = {
    page: 1,
    limit: 1000,
    search: '',
    fields: '',
  };

  private withInventoryScope(params: ToolSearchParams = {}): ToolSearchParams {
    const newParams: any = {
      ...params,
      'scope[inventory]': 1,
    };

    if (params.search && params.search.trim() !== '') {
      newParams['scope[inventory-search]'] = params.search.trim();
      delete newParams.search;
    }

    return newParams as ToolSearchParams;
  }



  private toolSearchParams: ToolSearchParams = {
    page: 1,
    limit: 20,
    search: '',
    fields: 'vehicleDetail.license_plate',
    order: 'toolId,desc',
    cache: 'skip',
    embed: 'media,vehicleDetail',
  };

  private defaultExportParams = {
    attributes: 'toolId,initialMileageHours,description,serialNumber',
  };

  private editToolModelParams: ToolSearchParams = {
    cache: 'clear',
    embed: this.editToolModelEmbeds,
  };

  private editModelListParams: ToolSearchParams = {
    cache: 'clear',
    embed: this.editModelListEmbeds,
    limit: 5000,
    search: 1,
    fields: 'enabled',
  };

  private editToolParams: ToolSearchParams = {
    cache: 'clear',
    embed: this.editToolEmbeds,
  };

  private defaultHistoryParams: ToolSearchParams = {
    page: 1,
    limit: 20,
    search: '',
    fields: 'type',
    order: 'toolId,desc',
    embed: '',
    requireAdditionalFields: true,
  };

  get editToolModelEmbeds() {
    const embeds = [
      'category',
      'manufacturer',
      'toolModel.thumbnail',
      'toolModel.inspection',
      'toolModel.maintenance',
      'toolModel.images',
      'toolModel.documents',
      'tools',
    ];
    return embeds.join(',');
  }

  get editModelListEmbeds() {
    const embeds = ['category'];
    return embeds.join(',');
  }

  get editToolEmbeds() {
    const embeds = [
      'model',
      'assignment',
      'tool.images',
      'tool.inspection',
      'tool.maintenance',
      'tool.documents',
      'vehicleDetail',
      'alert',
    ];
    return embeds.join(',');
  }

  get editToolModelAttributes() {
    const attributes = [
      'id',
      'name',
      'description',
      'notes',
      'modelYear',
      'placeholder',
      'categoryId',
      'manufacturerId',
      'status',
      'allowVehicleDetails',
      'rentalPriceDaily',
      'inspectionRequired',
      'maintenanceRequired',
      'rentalPriceWeekly',
      'rentalPriceMonthly',
      'category.id|name',
      'category.assetClass.id|name',
      'manufacturer.id|name',
    ];
    return attributes.join(',');
  }

  get editToolAttributes() {
    const attributes = [
      'id',
      'toolId',
      'description',
      'notes',
      'initialMileageHours',
      'lastMileageHours',
      'serialNumber',
      'purchDate',
      'assignmentId',
      'purchWarrantyExpiration',
      'maintenanceRequired',
      'inspectionRequired',
      'inspectionDate',
      'purchNote',
      'purchPrice',
      'purchPoNumber',
      'purchBy',
      'purchVendor',
      'status',
      'alert.id|message|enabled',
      'model.id|description|maintenanceRequired|inspectionRequired|modelYear|rentalPriceDaily|rentalPriceWeekly|rentalPriceMonthly',
      'model.category.id|name',
      'model.category.assetClass.id|name',
      'model.manufacturer.id|name',
    ];
    return attributes.join(',');
  }

  setTotalCount(value: number) {
    this.totalCountSource.next(value);
  }

  setTotalCountLabel(value: string) {
    this.totalCountLabelSource.next(value);
  }

  setCountShouldBeVisible(value: boolean) {
    this.countShouldBeVisibleSource.next(value);
  }

  //Custom duplicate endpoint
  duplicate = {
    models: (id: number): Observable<any> => this.postRequest('POST', `tools/models/${id}/duplicate`),
    tools: (id: number): Observable<any> => this.postRequest('POST', `tools/${id}/duplicate`),
  };

  history = {
    tools: (id: number, params: ToolSearchParams = this.defaultHistoryParams): Observable<any> =>
      this.postRequest('GET', `tools/${id}/history`, params),
    types: (): Observable<any> => this.postRequest('GET', 'tools/history/types'),
  };

  // READ endpoints
  read = {
    models: (params: ToolSearchParams = this.defaultParams): Observable<any> =>
      this.postRequest('GET', `tools/models`, params),
    tools: (params: ToolSearchParams = this.defaultParams): Observable<any> =>
      this.postRequest('GET', `tools`, params),
    transfers: (params: ToolSearchParams = this.defaultParams): Observable<any> =>
      this.postRequest('GET', `tools/history/assignments`, params),
    inventories: (params: ToolSearchParams = this.defaultParams): Observable<any> =>
      this.postRequest('GET', `tools/inventory`, this.withInventoryScope(params)),
  };

  readById = {
    models: (id: number, params: ToolSearchParams = this.editToolModelParams): Observable<any> =>
      this.postRequest('GET', `tools/models/${id}`, params),
    tools: (id: number | string, params: ToolSearchParams = this.editToolParams): Observable<any> =>
      this.postRequest('GET', `tools/${id}`, { ...params, embed: `${params.embed},toolInventory` }),
    inventories: (id: number, params: ToolSearchParams = this.defaultParams): Observable<any> =>
      this.postRequest('GET', `tools/inventory/${id}`, params),
  };

  // COUNT endpoints
  count = {
    models: (): Observable<any> => this.postRequest('GET', `tools/models/count`),
    tools: (): Observable<any> =>
      this.postRequest('GET', `tools/count`, { embed: 'assignment,model,alert,vehicleDetail' }),
    transfers: (): Observable<any> => this.postRequest('GET', `tools/history/assignments/count`),
    inventories: (params: ToolSearchParams = {}): Observable<any> =>
      this.postRequest('GET', `tools/inventory/count`, this.withInventoryScope(params)),
  };

  export = {
    tools: (params: ToolSearchParams = {}): Observable<any> => this.downloadRequest('POST', `tools/export`, params),
    'asset-classes': (params: ToolSearchParams = {}): Observable<any> => {
      return this.downloadRequest('POST', `tools/assets_classes/export`, params)
    },
    manufacturers: (params: ToolSearchParams = {}): Observable<any> =>
      this.downloadRequest('POST', `tools/manufacturers/export`, params),
    categories: (params: ToolSearchParams = {}): Observable<any> =>
      this.downloadRequest('POST', `tools/categories/export`, {
        ...params,
        embed: 'assetClass',
      }),
    'shop-locations': (params: ToolSearchParams = {}): Observable<any> =>
      this.downloadRequest('POST', 'shop_locations/export', params),
    models: (params: ToolSearchParams = {}): Observable<any> =>
      this.downloadRequest('POST', `tools/models/export`, params),
    transfers: (params: ToolSearchParams = {}): Observable<any> =>
      this.downloadRequest('POST', `tools/history/export`, params),
    inventories: (params: ToolSearchParams = {}): Observable<any> =>
      this.downloadRequest('GET', `tools/inventory/export`, this.withInventoryScope({ ...params, limit: 1000000 })),
  };

  // CREATE endpoints
  create = {
    models: (payload: any = {}): Observable<any> => this.postRequest('POST', `tools/models`, payload),
    tools: (payload: any = {}): Observable<any> => this.postRequest('POST', `tools`, payload),
    assignment: (payload: any = {}, queryString: string = ''): Observable<any> =>
      this.postRequest('POST', `tools/assignments${queryString ? '?' + queryString : ''}`, payload),
    inventory: (payload: any = {}, queryString: string = ''): Observable<any> =>
      this.postRequest('POST', `tools/inventory${queryString ? '?' + queryString : ''}`, payload),
  };

  // UPDATE endpoints
  update = {
    models: (payload: any = {}): Observable<any> => this.postRequest('PUT', `tools/models/${payload.id}`, payload.data),
    tools: (payload: any = {}): Observable<any> => this.postRequest('PUT', `tools/${payload.id}`, payload.data),
  };

  // ENABLE/DISABLE asset_class
  status = {
    models: (payload: any): Observable<any> =>
      this.postRequest('PATCH', `tools/models/${payload.id}`, {
        enabled: payload.data.enabled,
      }),
    tools: (payload: any): Observable<any> =>
      this.postRequest('PATCH', `tools/${payload.id}`, {
        enabled: payload.data.enabled,
      }),
  };

  refine = {
    models: (payload: any = {}): Observable<any> =>
      this.postRequest('GET', `tools/models/refine/${payload.scope}/${payload.value}`),
    tools: (payload: any = {}): Observable<any> =>
      this.postRequest('GET', `tools/models/refine/${payload.scope}/${payload.value}`),
  };

  // DELETE endpoints
  delete = {
    models: (id: number, params: any = undefined): any => this.postRequest('DELETE', `tools/models/${id}`, params),
    tools: (id: number, params: any = undefined): Observable<any> => this.postRequest('DELETE', `tools/${id}`, params),
  };

  list = {
    models: (params: any = this.editModelListParams): Observable<any> =>
      this.postRequest('GET', `tools/models`, { ...this.editModelListParams, ...params }),
    tools: (params: ToolSearchParams = this.defaultParams): Observable<any> => this.postRequest('GET', `tools`, params),
    jobs: (params: any): Observable<any> => this.postRequest('GET', 'jobs/v2', params),
    owners: (params: any): Observable<any> => this.postRequest('GET', 'owner/v2', params),
    facilities: (params: any): Observable<any> => this.postRequest('GET', 'owner_and_facilities/v2', params),
    users: (params: any): Observable<any> => this.postRequest('GET', 'users/v2', params),
    shops: (params: any = {}): Observable<any> =>
      this.postRequest('GET', 'shop_locations', {
        ...this.defaultShopParams,
        ...params,
      }),
    vendors: (params: any): Observable<any> => this.postRequest('GET', 'vendors/v2', params),
    assignment_types: (params: any): Observable<any> => this.postRequest('GET', 'tools/assignment_types', params),
  };

  transferList = {
    jobs: (params: any): Observable<any> => this.postRequest('GET', 'jobs/v2', params),
    owners: (params: any): Observable<any> => this.postRequest('GET', 'owner/v2', params),
    facilities: (params: any): Observable<any> => this.postRequest('GET', 'owner_and_facilities/v2', params),
    users: (params: any): Observable<any> => this.postRequest('GET', 'users/v2', params),
    shops: (params: any): Observable<any> => this.postRequest('GET', 'shop_locations', params),
    vendors: (params: any): Observable<any> => this.postRequest('GET', 'vendors/v2', params),
  };

  generateTransferBatchID() {
    return new Promise((resolve, reject) => {
      this.postRequest('GET', 'tools/assignments/generateBatchId').subscribe(
        res => {
          resolve(res.data.batchId);
        },
        error => reject(error),
      );
    });
  }

  add(item) {
    const currentList = this.selectedItemsSource.value;
    let updatedList = [...currentList, item];
    updatedList = this.filterDuplicates(updatedList);
    this.selectedItemsSource.next(updatedList);
    this.saveToLocalStorage();
  }

  remove(id: number) {
    const currentList = this.selectedItemsSource.value;
    const updatedList = currentList.filter(row => {
      return row.id !== id;
    });
    this.selectedItemsSource.next(updatedList);
    this.saveToLocalStorage();
  }

  saveToLocalStorage() {
    localStorage.setItem('selected-jobs', JSON.stringify(this.selectedItemsSource.value));
  }

  filterDuplicates(list: Array<any>) {
    return list.filter((e, i) => list.findIndex(obj => obj.id == e.id) === i);
  }

  private postRequest(
    requestType: string,
    url: string,
    data: any = '',
    showLoader: boolean = false,
    hideLoader: boolean = false,
  ): Observable<any> {
    // if GET request and param data was received, format query string and reset data to be sent in body
    if (requestType === 'GET' && data) {
      const queryString = buildQueryString(data);
      url += queryString;
      data = '';
    }

    this.HttpRequestModel.reset();
    this.HttpRequestModel.method = requestType;
    this.HttpRequestModel.url = url;
    this.HttpRequestModel.body = data;

    return this.httpService.init(this.HttpRequestModel, showLoader, hideLoader).pipe(map(response => response));
  }
  private downloadRequest(
    requestType: string,
    url: string,
    data: any = '',
    showLoader: boolean = false,
    hideLoader: boolean = false,
  ): Observable<any> {
    if (data) {
      const queryString = buildQueryString(data);
      url += queryString;
      data = '';
    }
    this.HttpRequestModel.reset();
    this.HttpRequestModel.method = requestType;
    this.HttpRequestModel.url = url;
    this.HttpRequestModel.responseType = 'blob';
    this.HttpRequestModel.observe = 'response';

    return this.httpService.init(this.HttpRequestModel, showLoader, hideLoader).pipe(map(response => response));
  }
}
